<template>
  <div class="new-production page">
    <section class="hero">
      <div class="hero-body">
        <h2 class="subtitle">
          {{ $t('productions.creation.new_project') }}
        </h2>
        <h1 class="title" @click="debugClick">
          {{ $t('productions.creation.create_production') }}
        </h1>
      </div>
    </section>

    <div class="tabs mt1">
      <ul>
        <li
          :class="{'is-active': isActiveTab('parameters')}"
          @click="setActiveTab('parameters')"
        >
          <check-icon
            class="check"
            size="25"
            v-if="hasValidName && hasValidSettings"
          />
          <span class="step" v-else>1</span>
          {{ $t('productions.creation.parameters')}}
        </li>
        <li
          :class="{'is-active': isActiveTab('asset_task_types')}"
          @click="setActiveTab('asset_task_types')"
        >
          <check-icon
            class="check"
            size="25"
            v-if="hasValidAssetTaskTypes"
          />
          <span class="step" v-else>2</span>
          {{ $t('productions.creation.asset_task_types')}}
        </li>
        <li
          :class="{'is-active': isActiveTab('shot_task_types')}"
          @click="setActiveTab('shot_task_types')"
        >
          <check-icon
            class="check"
            size="25"
            v-if="hasValidShotTaskTypes"
          />
          <span class="step" v-else>3</span>
          {{ $t('productions.creation.shot_task_types')}}
        </li>
        <li
          :class="{'is-active': isActiveTab('team')}"
          @click="setActiveTab('team')"
        >
          <check-icon
            class="check"
            size="25"
            v-if="hasValidTeam"
          />
          <span class="step team" v-else>4</span>
          {{ $t('people.team')}}
        </li>
        <li
          :class="{'is-active': isActiveTab('optional')}"
          @click="setActiveTab('optional')"
        >
          <check-icon
            class="check"
            size="25"
            v-if="hasValidTaskStatuses && hasValidAssetTypes && hasValidAssets && hasValidShots"
          />
          <span class="step optional" v-else>5</span>
          {{ $t('productions.creation.optional')}}
        </li>
      </ul>
    </div>

    <div v-if="isActiveTab('parameters')">
      <h1>{{ $t('productions.creation.production_settings') }}</h1>
      <p>{{ $t('productions.creation.production_settings_description') }}</p>
      <!-- :is-completed="hasValidName && hasValidSettings" -->
      <div class="flexrow">
        <!-- <text-field
          class="number-field flexrow-item"
          input-class="is-small is-inline"
          :label="$t('productions.metadata.number')"
          v-model="productionToCreate.number"
        /> -->
        <text-field
          class="flexrow-item"
          input-class="w600 is-inline"
          :select-on-click="true"
          :label="$t('productions.fields.name')"
          :placeholder="$t('productions.creation.placeholder_name')"
          v-model="productionToCreate.name"
        />
      </div>
      <div>
        <combobox
          class="type-combo"
          :options="productionTypeOptions"
          :label="$t('productions.fields.type')"
          localeKeyPrefix="productions.type."
          v-model="productionToCreate.settings.type"
          thin
        />
      </div>
      <div class="mb1 explaination">
        {{ $t('productions.creation.explaination_type') }}
      </div>
      <div class="flexrow">
        <text-field
          class="flexrow-item"
          input-class=" is-small is-size-4"
          :label="$t('productions.fields.fps')"
          type="number"
          :max="60"
          :step="0.01"
          :placeholder="$t('productions.creation.placeholder_fps')"
          :errored="!hasValidFPS"
          v-model="productionToCreate.settings.fps"
          thin
        />
        <span class="input-separator flexrow-item mr1"></span>
        <text-field
          class="flexrow-item mr0"
          input-class=" is-small is-size-2"
          :label="$t('productions.fields.ratio')"
          type="number"
          :step="1"
          :placeholder="$t('productions.creation.placeholder_ratio1')"
          v-model="productionToCreate.settings.ratio[0]"
          thin
        />
        <span class="input-separator flexrow-item mt15 mr0">:</span>
        <text-field
          class="flexrow-item"
          input-class=" is-small is-size-2"
          type="number"
          :step="1"
          :placeholder="$t('productions.creation.placeholder_ratio2')"
          :empty-label="true"
          v-model="productionToCreate.settings.ratio[1]"
          thin
        />
        <span class="input-separator flexrow-item mr1"></span>
        <text-field
          class="flexrow-item mr0 ml2"
          input-class=" is-small is-size-4"
          :label="$t('productions.fields.resolution')"
          type="number"
          :step="1"
          :placeholder="$t('productions.creation.placeholder_resolution1')"
          v-model="productionToCreate.settings.resolution[0]"
          thin
        />
        <span class="input-separator flexrow-item mt15 mr05">x</span>
        <text-field
          class="flexrow-item"
          input-class=" is-small is-size-4"
          type="number"
          :placeholder="$t('productions.creation.placeholder_resolution2')"
          :step="1"
          :empty-label="true"
          v-model="productionToCreate.settings.resolution[1]"
          thin
        />
      </div>
      <div class="mb1 explaination">
        {{ $t('productions.creation.explaination_video') }}
      </div>
      <div>
        <label class="label">
          {{
            $t('productions.creation.start_and_end_dates')
          }}
        </label>
        <div class="date-picker-wrapper">
        <datepicker
          wrapper-class="datepicker"
          input-class=" is-small date-input input"
          label="Start date"
          :placeholder="startDatePlaceholder"
          :language="locale"
          :disabled-dates="{ days: [6, 0] }"
          :monday-first="true"
          format="yyyy-MM-dd"
          v-model="productionToCreate.settings.dateStart"
        />
        <span class="input-separator">-</span>
        <datepicker
          wrapper-class="datepicker"
          input-class="is-small date-input input"
          :language="locale"
          :disabled-dates="{ days: [6, 0] }"
          :placeholder="endDatePlaceholder"
          :monday-first="true"
          :disabledDates="{ to: productionToCreate.settings.dateStart }"
          format="yyyy-MM-dd"
          v-model="productionToCreate.settings.dateEnd"
        />
        </div>
      </div>
      <div class="mb1 explaination">
        {{ $t('productions.creation.explaination_date') }}
      </div>
    </div>
    <div v-if="isActiveTab('asset_task_types')">
      <h1>{{ $t('productions.creation.select_asset_task_type') }}</h1>
      <p>{{ $t('productions.creation.select_asset_task_type_description') }}</p>
      <!-- :is-completed="hasValidAssetTaskTypes" -->
      <!-- <draggable
        v-model="productionToCreate.assetTaskTypes"
        draggable=".task-type"
      >
        <task-type-name
          class="task-type"
          :task-type="taskType"
          :key="taskType.id"
          deletable
          @delete="deleteFromList(taskType, 'assetTaskTypes')"
          v-for="taskType in productionToCreate.assetTaskTypes"
        />
        <combobox-task-type
          slot="footer"
          class="is-inline inline-task-type-combo"
          v-model="availableAssetTaskTypes"
          add-placeholder
          @add="id => productionToCreate.assetTaskTypes.push(
            taskTypeMap.get(id)
          )"
          v-if="availableAssetTaskTypes.length > 0"
        />
      </draggable> -->
      <task-type-checklist
        v-model="sortedAssetTaskTypes"
        @add="id => {
          productionToCreate.assetTaskTypes.push(taskTypeMap.get(id))
        }"
        @remove="id => {
          deleteFromList(taskTypeMap.get(id), 'assetTaskTypes')
        }"
      />
    </div>
    <div v-if="isActiveTab('shot_task_types')">
      <h1>{{ $t('productions.creation.select_shot_task_type') }}</h1>
      <p>{{ $t('productions.creation.select_shot_task_type_description') }}</p>
      <!-- :is-completed="hasValidShotTaskTypes" -->
      <task-type-checklist
        v-model="sortedShotTaskTypes"
        @add="id => {
          productionToCreate.shotTaskTypes.push(taskTypeMap.get(id))
        }"
        @remove="id => {
          deleteFromList(taskTypeMap.get(id), 'shotTaskTypes')
        }"
      />
    </div>
    <div v-if="isActiveTab('team')">
      <h1>{{ $t('productions.creation.team') }}</h1>
      <p>{{ $t('productions.creation.team_description') }}</p>
      <people-checklist
        :people="people"
        :selected="[ user ]"
        @select="(id) => {productionToCreate.team.push(id)}"
        @remove="(id) => {removePerson(id)}"
      />
    </div>
    <div v-if="isActiveTab('optional')">
      <h1>{{ $t('productions.creation.select_task_status') }}</h1>
      <p>{{ $t('productions.creation.select_task_status_description') }}</p>
      <!-- :is-completed="hasValidTaskStatuses && hasValidAssetTypes" -->
      <!-- :is-completed="hasValidAssets && hasValidShots" -->
      <div class="flexrow">
        <validation-tag
          class="task-status flexrow-item"
          :task="{ task_status_id: taskStatus.id }"
          :key="taskStatus.id"
          pointer
          is-static
          @click="deleteFromList(taskStatus, 'taskStatuses')"
          v-for="taskStatus in productionToCreate.taskStatuses"
        />
        <combobox-status
          slot="footer"
          class="flexrow-item"
          :task-status-list="availableTaskStatuses"
          :with-margin="false"
          add-placeholder
          @input="id => productionToCreate.taskStatuses.push(
            taskStatusMap.get(id)
          )"
          v-if="availableTaskStatuses.length > 0"
        />
      </div>

      <h1>{{ $t('productions.creation.add_asset_types') }}</h1>
      <p>{{ $t('productions.creation.add_asset_types_description') }}</p>
      <div class="flexrow asset-types mb1">
        <span
          :key="assetType.id"
          class="asset-type-name flexrow-item"
          @click="deleteFromList(assetType, 'assetTypes')"
          v-for="assetType in productionToCreate.assetTypes"
        >
          {{ assetType.name }}
        </span>
        <combobox
          class="flexrow-item"
          :options="availableAssetTypes"
          :with-margin="false"
          @input="id => {
            assetTypeMap.get(id) && productionToCreate.assetTypes.push(
              assetTypeMap.get(id)
            )
          }"
          v-if="availableAssetTypes.length > 1"
        />
      </div>

      <h1>{{ $t('productions.creation.add_assets') }}</h1>
      <p>{{ $t('productions.creation.add_assets_description') }}</p>
      <div class="import-content">
        <span class="tag mr1" v-if="nbAssetsToImport > 0">
          {{ nbAssetsToImport }}
          {{ $t('productions.creation.assets_to_import') }}
        </span>
        <button
          class="button import-button"
          @click="toggleModal('isAssetsImportDisplayed')"
        >
          {{ $t('productions.creation.import_assets_button') }}
        </button>
      </div>

      <h1>{{ $t('productions.creation.add_shots') }}</h1>
      <p>{{ $t('productions.creation.add_shots_description') }}</p>
      <div class="import-content">
        <span class="tag mr1" v-if="nbShotsToImport > 0">
          {{ nbShotsToImport }}
          {{ $t('productions.creation.shots_to_import') }}
        </span>
        <button
          class="button import-button"
          @click="toggleModal('isShotsImportDisplayed')"
        >
          {{ $t('productions.creation.import_shots_button') }}
        </button>
<!--            <button-->
<!--              class="button ml1"-->
<!--              @click="toggleModal('isAddShotsDisplayed')"-->
<!--            >-->
<!--              + {{ $t('productions.creation.add_shots_button') }}-->
<!--            </button>-->
      </div>
    </div>
    <section class="has-text-centered mt2">
      <p v-if="errors.importingAssets" class="error">
        {{ $t('productions.creation.errorImportingAssets') }}
        <br>
        {{ errors.importingAssetsError }}
      </p>
      <p v-if="errors.importingShots" class="error">
        {{ $t('productions.creation.errorImportingShots') }}
        <br>
        {{ errors.importingShotsError }}
      </p>
      <p v-if="errors.creatingProduction" class="error">
        {{ $t('productions.creation.error') }}
        {{ errors.creatingProductionError }}
      </p>
      <button
        :class="{
          button: true,
          'active-confirm': hasAllDataCorrect,
          'is-disabled': !hasAllDataCorrect
        }"
        @click="createProduction"
      >
        <spinner
          class="mr05 mt05"
          :size="20"
          is-white
          v-if="loading.createProduction"
        />
        {{ $t('main.confirmation') }}
      </button>
      <button-link
        class="button is-link"
        :text="$t('main.cancel')"
        path="/productions"
      />
    </section>

    <import-render-modal
      :active="modals.isAssetsImportRenderDisplayed"
      :is-loading="loading.importingAssets"
      :is-error="errors.importingAssets"
      :import-error="errors.importingAssetsError"
      :parsed-csv="parsedAssetsCSV"
      :form-data="assetsCsvFormData"
      :columns="assetsRenderColumns"
      :data-matchers="assetsDataMatchers"
      :database="{}"
      :disable-update="true"
      @reupload="resetAssetsImport"
      @confirm="uploadAssetsImportFile"
      @cancel="toggleModal('isAssetsImportRenderDisplayed')"
    />

    <import-modal
      ref="import-assets-modal"
      :active="modals.isAssetsImportDisplayed"
      :is-loading="loading.importingAssets"
      :is-error="errors.importingAssets"
      :form-data="assetsCsvFormData"
      :columns="assetsDataMatchers"
      :optional-columns="assetsOptionalColumns"
      :generic-columns="genericColumns"
      @confirm="renderAssetsImport"
      @cancel="toggleModal('isAssetsImportDisplayed')"
    />

    <import-render-modal
      :active="modals.isShotsImportRenderDisplayed"
      :is-loading="loading.importingShots"
      :is-error="errors.importingShots"
      :import-error="errors.importingShotsError"
      :parsed-csv="parsedShotsCSV"
      :form-data="shotsCsvFormData"
      :columns="shotsRenderColumns"
      :data-matchers="shotsDataMatchers"
      :database="{}"
      :disable-update="true"
      @reupload="resetShotsImport"
      @confirm="uploadShotsImportFile"
      @cancel="toggleModal('isShotsImportRenderDisplayed')"
    />

    <import-modal
      ref="import-shots-modal"
      :active="modals.isShotsImportDisplayed"
      :is-loading="loading.importingShots"
      :is-error="errors.importingShots"
      :form-data="shotsCsvFormData"
      :columns="shotsDataMatchers"
      :optional-columns="shotsOptionalColumns"
      :generic-columns="genericColumns"
      @confirm="renderShotsImport"
      @cancel="toggleModal('isShotsImportDisplayed')"
    />

    <manage-shots-modal
      :active="modals.isAddShotsDisplayed"
      @add-episode="addEpisode"
      @add-sequence="addSequence"
      @add-shot="addShot"
      @cancel="toggleModal('isAddShotsDisplayed')"
    />
  </div>
</template>

<script>
import Datepicker from 'vuejs-datepicker'
import { CheckIcon } from 'vue-feather-icons'
import moment from 'moment'
import { en, fr } from 'vuejs-datepicker/dist/locale'
import { mapActions, mapGetters } from 'vuex'

import csv from '@/lib/csv'
import func from '@/lib/func'
import { removeModelFromList } from '@/lib/models'
import { formatSimpleDate } from '@/lib/time'
import { sortByName } from '@/lib/sorting'
import {
  PRODUCTION_STYLE_OPTIONS,
  PRODUCTION_TYPE_OPTIONS
} from '@/lib/productions'

import ButtonLink from '@/components/widgets/ButtonLink'
import Combobox from '@/components/widgets/Combobox'
import ComboboxStatus from '@/components/widgets/ComboboxStatus'
import ImportModal from '@/components/modals/ImportModal'
import ImportRenderModal from '@/components/modals/ImportRenderModal'
import ManageShotsModal from '@/components/modals/ManageShotsModal'
import Spinner from '@/components/widgets/Spinner'
import TaskTypeChecklist from '@/components/lists/TaskTypeChecklist'
import PeopleChecklist from '@/components/lists/PeopleChecklist'
import TextField from '@/components/widgets/TextField'
import ValidationTag from '@/components/widgets/ValidationTag'

const VALID_NAME_REGEX = /^\d{2}_\d{3}_[a-z0-9_]+$/

export default {
  name: 'NewProduction',
  components: {
    ButtonLink,
    CheckIcon,
    Combobox,
    ComboboxStatus,
    Datepicker,
    ImportModal,
    ImportRenderModal,
    ManageShotsModal,
    Spinner,
    TaskTypeChecklist,
    PeopleChecklist,
    TextField,
    ValidationTag
  },
  data() {
    return {
      activeTab: 'parameters',
      adjectives: [
        'awesome',
        'amazeballs',
        'bodacious',
        'dazzling',
        'illustrious',
        'majestic',
        'magnificent',
        'jolly',
        'stupendous',
        'prodigious',
      ],
      tabOptions: [
        'parameters',
        'asset_task_types',
        'shot_task_types',
        'optional'
      ],
      errors: {
        creatingProduction: false,
        creatingProductionError: '',
        importingAssets: false,
        importingAssetsError: null,
        importingShots: false,
        importingShotsError: null
      },
      loading: {
        createProduction: false,
        importingAssets: false,
        importingShots: false
      },
      modals: {
        isAddAssetsDisplayed: false,
        isAddShotsDisplayed: false,
        isAssetsImportDisplayed: false,
        isAssetsImportRenderDisplayed: false,
        isShotsImportDisplayed: false,
        isShotsImportRenderDisplayed: false
      },
      parsedAssetsCSV: [],
      parsedShotsCSV: [],
      productionToCreate: {
        assetsToAdd: null,
        assetTypes: [],
        assetTaskTypes: [],
        episodesToCreate: [],
        name: null,
        number: null,
        sequencesToCreate: [],
        settings: {
          dateStart: moment().toDate(),
          dateEnd: moment().add(3, 'month').toDate(),
          fps: 25, // eg: '24'
          ratio: [16, 9], // eg: [4, 3]
          resolution: [1920, 1080], // eg: [1440, 1080]
          style: PRODUCTION_STYLE_OPTIONS[0].value,
          type: PRODUCTION_TYPE_OPTIONS[0].value
        },
        shotsToAdd: null,
        shotTaskTypes: [],
        shotsToCreate: [],
        team: [],
        taskStatuses: []
      },
      assetsOptionalColumns: ['Description', 'Ready for'],
      shotsOptionalColumns: [
        'Description',
        'Nb Frames',
        'Frame In',
        'Frame Out',
        'FPS'
      ],
      genericColumns: [
        'metadata_column_name => text value',
        'task_type_name => task_status_name',
        'task_type_name comment => comment text'
      ],
      productionStyleOptions: PRODUCTION_STYLE_OPTIONS,
      productionTypeOptions: PRODUCTION_TYPE_OPTIONS
    }
  },

  computed: {
    ...mapGetters([
      'assetsCsvFormData',
      'assetTaskTypes',
      'assetTypeMap',
      'assetTypes',
      'people',
      'personMap',
      'productions',
      'productionStatus',
      'shotsCsvFormData',
      'shotTaskTypes',
      'taskStatus',
      'taskStatusMap',
      'taskTypeMap',
      'user'
    ]),

    isTVShow() {
      return this.productionToCreate.settings.type === 'tvshow'
    },

    assetsDataMatchers() {
      return this.isTVShow ? ['Episode', 'Type', 'Name'] : ['Type', 'Name']
    },

    assetsRenderColumns() {
      var collection = [
        ...this.assetsDataMatchers,
        ...this.assetsOptionalColumns
      ]

      this.productionToCreate.assetTaskTypes.forEach(item => {
        collection.push(item.name)
        collection.push(item.name + ' comment')
      })

      return collection
    },

    shotsRenderColumns() {
      var collection = [...this.shotsDataMatchers, ...this.shotsOptionalColumns]

      this.productionToCreate.shotTaskTypes.forEach(item => {
        collection.push(item.name)
        collection.push(item.name + ' comment')
      })

      return collection
    },

    shotsDataMatchers() {
      return this.isTVShow
        ? ['Episode', 'Sequence', 'Name']
        : ['Sequence', 'Name']
    },

    sortedAssetTaskTypes () {
      return this.sortByDepartment(this.assetTaskTypes)
    },

    sortedShotTaskTypes () {
      return this.sortByDepartment(this.shotTaskTypes)
    },

    locale () {
      if (this.user.locale === 'fr_FR') {
        return fr
      } else {
        return en
      }
    },

    allowedProductionTypes() {
      return PRODUCTION_TYPE_OPTIONS.map(option => option.value)
    },

    hasUniqueNumber() {
      return this.productions.findIndex(p => p.number === this.productionToCreate.number) === -1
    },

    hasValidName() {
      return this.hasUniqueNumber
        && !this.isEmpty(this.productionToCreate.name)
        && this.productionToCreate.name.match(VALID_NAME_REGEX)
    },

    hasValidStartDate() {
      return !this.isEmpty(this.productionToCreate.settings.dateStart)
    },

    hasValidEndDate() {
      return !this.isEmpty(this.productionToCreate.settings.dateEnd)
    },

    hasValidAssets() {
      return this.nbAssetsToImport > 0
    },

    hasValidAssetTypes() {
      return this.productionToCreate.assetTypes.length > 0
    },

    hasValidShots() {
      return this.nbShotsToImport > 0
    },

    hasValidTeam () {
      return true
      return this.productionToCreate.team.length > 0
    },

    hasValidSettings () {
      return (
        this.hasValidType &&
        this.hasValidFPS &&
        this.hasValidRatio &&
        this.hasValidResolution &&
        this.hasValidStartDate &&
        // this.hasValidTeam &&
        this.hasValidEndDate
      )
    },

    hasValidFPS() {
      const fps = parseInt(this.productionToCreate.settings.fps)
      return fps > 0 && fps <= 60
    },

    hasValidRatio() {
      if (this.isEmpty(this.productionToCreate.settings.ratio)) {
        return false
      }
      return (
        this.productionToCreate.settings.ratio.length === 2 &&
        this.isFloat(this.productionToCreate.settings.ratio[0]) &&
        this.isInteger(this.productionToCreate.settings.ratio[1])
      )
    },

    hasValidResolution() {
      if (this.isEmpty(this.productionToCreate.settings.resolution)) {
        return false
      }
      return (
        this.productionToCreate.settings.resolution.length === 2 &&
        this.isInteger(this.productionToCreate.settings.resolution[0]) &&
        this.isInteger(this.productionToCreate.settings.resolution[1])
      )
    },

    hasValidType() {
      return (
        this.allowedProductionTypes.indexOf(
          this.productionToCreate.settings.type
        ) !== -1
      )
    },

    hasValidAssetTaskTypes() {
      return true
      return this.productionToCreate.assetTaskTypes.length > 0
    },

    hasValidShotTaskTypes() {
      return true
      return this.productionToCreate.shotTaskTypes.length > 0
    },

    hasValidTaskStatuses() {
      return this.productionToCreate.taskStatuses.length > 0
    },

    hasAllDataCorrect() {
      return (
        this.hasValidName &&
        this.hasValidSettings &&
        (
          this.hasValidAssetTaskTypes ||
          this.productionToCreate.settings.type === 'shots'
        ) &&
        (
          this.hasValidShotTaskTypes ||
          this.productionToCreate.settings.type === 'assets'
        ) &&
        this.hasValidTaskStatuses &&
        (
          this.hasValidAssetTypes ||
          this.productionToCreate.settings.type === 'shots'
        ) &&
        this.hasValidTeam
      )
    },

    availableAssetTaskTypes() {
      return this.assetTaskTypes.filter(
        assetTaskType =>
          this.productionToCreate.assetTaskTypes.indexOf(assetTaskType) === -1
      )
    },

    availableAssetTypes() {
      const assetTypes = sortByName(
        this.assetTypes.filter(assetType => {
          return this.productionToCreate.assetTypes.indexOf(assetType) === -1
        })
      )
      return [
        {
          name: '+ Asset Type',
          id: '-'
        },
        ...assetTypes
      ].map(assetType => {
        return {
          label: assetType.name,
          value: assetType.id
        }
      })
    },

    availableShotTaskTypes() {
      return this.shotTaskTypes.filter(
        shotTaskType =>
          this.productionToCreate.shotTaskTypes.indexOf(shotTaskType) === -1
      )
    },

    availableTaskStatuses() {
      return this.taskStatus.filter(
        status =>
          this.productionToCreate.taskStatuses.indexOf(status) === -1 &&
          !status.is_default
      )
    },

    nbAssetsToImport() {
      if (this.productionToCreate.assetsToAdd) {
        const assetsLines = this.productionToCreate.assetsToAdd.filter(
          assetLine => assetLine.length > 1
        )
        return assetsLines.length - 1
      }
      return 0
    },

    nbShotsToImport() {
      let nbShots = this.productionToCreate.shotsToCreate.length
      if (this.productionToCreate.shotsToAdd) {
        const shotsLines = this.productionToCreate.shotsToAdd.filter(
          shotLine => shotLine.length > 1
        )
        nbShots += shotsLines.length - 1
      }
      return nbShots
    },

    startDatePlaceholder() {
      return formatSimpleDate(moment())
    },

    endDatePlaceholder() {
      return formatSimpleDate(moment().add(3, 'month'))
    }
  },

  methods: {
    ...mapActions([
      'addAssetTypeToProduction',
      'addPersonToTeam',
      'addTaskStatusToProduction',
      'addTaskTypeToProduction',
      'loadContext',
      'newProduction',
      'setProduction',
      'uploadAssetFile',
      'uploadShotFile'
    ]),
    removeModelFromList,

    debugClick() {
      console.log(this.productionToCreate.assetTypes)
      console.log(this.productionToCreate.taskStatuses)
    },

    getAdjective() {
      return this.adjectives[Math.floor(Math.random() * this.adjectives.length)]
    },

    deleteFromList(object, listName) {
      this.productionToCreate[listName] = removeModelFromList(
        this.productionToCreate[listName],
        object
      )
    },

    isActiveTab (tabName) {
      return this.activeTab === tabName
    },

    removePerson (personId) {
      const index = this.productionToCreate.team.find((id) => { return personId === id })
      if (index !== undefined) {
        this.productionToCreate.team.splice(index, 1)
      }
    },

    setActiveTab (tabName) {
      this.activeTab = tabName
    },

    sortByDepartment (taskTypeList) {
      const res = {}
      taskTypeList.forEach((taskType) => {
        if (res[taskType.department_id]) {
          res[taskType.department_id].push(taskType)
        } else {
          res[taskType.department_id] = [taskType]
        }
      })
      return res
    },

    isEmpty (value) {
      return (
        value === null ||
        value === undefined ||
        (typeof value === 'object' && !(value instanceof Date) && Object.keys(value).length === 0)
      )
    },

    isFloat(value) {
      return !this.isEmpty(value) && /^[(\d)*,(\d)+]|(\d)+$/.test(value)
    },

    isInteger(value) {
      return !this.isEmpty(value) && /^\d+$/.test(value)
    },

    async createTaskTypesAndStatuses() {
      await func.runPromiseAsSeries(
        this.productionToCreate.assetTaskTypes
          .concat(this.productionToCreate.shotTaskTypes)
          .map(
            // add task types
            async (taskType, index) => {
              const finalIndex =
                taskType.for_entity === 'Shot'
                  ? index - this.productionToCreate.assetTaskTypes.length
                  : index
              return await this.addTaskTypeToProduction({
                taskTypeId: taskType.id,
                priority: finalIndex + 1
              })
            }
          )
          .concat(
            // add task statuses
            this.productionToCreate.taskStatuses.map(async taskStatus => {
              return await this.addTaskStatusToProduction(taskStatus.id)
            })
          )
      )
    },

    async createAssets() {
      if (this.productionToCreate.assetsToAdd !== null) {
        this.loading.importingAssets = true
        this.errors.importingAssets = false
        await this.uploadAssetFile(false)
          .then(() => {
            this.loading.importingAssets = false
          })
          .catch(err => {
            this.loading.importingAssets = false
            this.errors.importingAssets = true
            this.errors.importingAssetsError = err
          })
      }
    },

    createAssetTypes() {
      this.productionToCreate.assetTypes.map(async assetType => {
        return await this.addAssetTypeToProduction(assetType.id)
      })
    },

    async createShots() {
      if (this.productionToCreate.shotsToAdd !== null) {
        this.loading.importingShots = true
        this.errors.importingShots = false
        await this.uploadShotFile(false)
          .then(() => {
            this.loading.importingShots = false
          })
          .catch(err => {
            this.loading.importingShots = false
            this.errors.importingShots = true
            this.errors.importingShotsError = err
          })
      }
    },

    createProductionRoute(createdProduction) {
      const params = {
        production_id: createdProduction.id
      }
      let routeName = 'assets'
      if (this.isTVShow) {
        params.episode_id = 'all'
        routeName = 'episode-assets'
      }
      return {
        name: routeName,
        params
      }
    },

    async createProduction() {
      if (this.loading.createProduction) return
      this.loading.createProduction = true
      this.errors.creatingProduction = false
      this.errors.creatingProductionError = ''
      try {
        const createdProduction = await this.newProduction({
          name: this.productionToCreate.name,
          number: this.productionToCreate.number,
          project_status_id: this.productionStatus[0].id,
          fps: this.productionToCreate.settings.fps,
          ratio: this.productionToCreate.settings.ratio.join(':'),
          resolution: this.productionToCreate.settings.resolution.join('x'),
          production_type: this.productionToCreate.settings.type,
          production_style: this.productionToCreate.settings.style,
          start_date: this.productionToCreate.settings.dateStart,
          end_date: this.productionToCreate.settings.dateEnd
        })
        await this.setProduction(createdProduction.id)
        await this.createTaskTypesAndStatuses()
        await this.createAssetTypes()
        await this.createAssets()
        if (this.productionToCreate.production_type !== 'assets') {
          await this.createShots()
        }
        await this.loadContext()
        await this.productionToCreate.team.forEach((persId) => {
          const person = this.personMap.get(persId)
          if (person) {
            this.addPersonToTeam(person)
          }
        })
        await this.$router.push(this.createProductionRoute(createdProduction))
      } catch (error) {
        console.error(error, error.response)
        this.errors.creatingProduction = true
        this.errors.creatingProductionError = error.response
          ? ': ' + error.response.body.message.substring(0, 165)
          : ''
      }
      this.loading.createProduction = false
    },

    toggleModal(modalName) {
      this.modals[modalName] = !this.modals[modalName]
    },

    renderAssetsImport(data, mode) {
      this.loading.importingAssets = true
      this.errors.importingAssets = false
      if (mode === 'file') {
        data = data.get('file')
      }
      csv.processCSV(data).then(results => {
        this.parsedAssetsCSV = results
        this.toggleModal('isAssetsImportDisplayed')
        this.loading.importingAssets = false
        this.toggleModal('isAssetsImportRenderDisplayed')
      })
    },

    resetAssetsImport() {
      this.errors.importingAssets = false
      this.toggleModal('isAssetsImportRenderDisplayed')
      this.$store.commit('ASSET_CSV_FILE_SELECTED', null)
      this.productionToCreate.assetsToAdd = null
      this.$refs['import-assets-modal'].reset()
      this.toggleModal('isAssetsImportDisplayed')
    },

    uploadAssetsImportFile(data, toUpdate) {
      const formData = new FormData()
      const filename = 'import.csv'
      const csvContent = csv.turnEntriesToCsvString(data)
      const file = new File([csvContent], filename, { type: 'text/csv' })

      formData.append('file', file)

      this.$store.commit('ASSET_CSV_FILE_SELECTED', formData)
      this.productionToCreate.assetsToAdd = data
      this.toggleModal('isAssetsImportRenderDisplayed')
    },

    renderShotsImport(data, mode) {
      this.loading.importingShots = true
      this.errors.importingShots = false
      if (mode === 'file') {
        data = data.get('file')
      }
      csv.processCSV(data).then(results => {
        this.parsedShotsCSV = results
        this.toggleModal('isShotsImportDisplayed')
        this.loading.importingShots = false
        this.toggleModal('isShotsImportRenderDisplayed')
      })
    },

    resetShotsImport() {
      this.errors.importingShots = false
      this.toggleModal('isShotsImportRenderDisplayed')
      this.$store.commit('SHOT_CSV_FILE_SELECTED', null)
      this.productionToCreate.shotsToAdd = null
      this.$refs['import-shots-modal'].reset()
      this.toggleModal('isShotsImportDisplayed')
    },

    uploadShotsImportFile(data, toUpdate) {
      const formData = new FormData()
      const filename = 'import.csv'
      const csvContent = csv.turnEntriesToCsvString(data)
      const file = new File([csvContent], filename, { type: 'text/csv' })

      formData.append('file', file)

      this.$store.commit('SHOT_CSV_FILE_SELECTED', formData)
      this.productionToCreate.shotsToAdd = data
      this.toggleModal('isShotsImportRenderDisplayed')
    },

    addEpisode(episode, callback) {
      this.productionToCreate.episodesToCreate.push(episode)
      episode.id = this.productionToCreate.episodesToCreate.length - 1
      callback(episode)
    },

    addSequence(sequence, callback) {
      this.productionToCreate.sequencesToCreate.push(sequence)
      sequence.id = this.productionToCreate.sequencesToCreate.length - 1
      callback(sequence)
    },

    addShot(shot, callback) {
      this.productionToCreate.shotsToCreate.push(shot)
      shot.id = this.productionToCreate.shotsToCreate.length - 1
      callback(shot)
    }
  },

  mounted () {
    this.productionToCreate.taskStatuses = this.availableTaskStatuses
    for(const ass of this.availableAssetTypes) {
      if (this.assetTypeMap.get(ass.value)) {
        this.productionToCreate.assetTypes.push(this.assetTypeMap.get(ass.value))
      }
    }
    const currentyear = moment().format('YY')
    const numbers = this.productions.filter(p => p.number.slice(0,2) === currentyear)
      .map(p => parseInt(p.number.slice(3,6)))
    const nextnumber = numbers.length > 0 ? Math.max(...numbers) + 1 : 1
    this.productionToCreate.number = `${currentyear}_${(nextnumber).toString().padStart(3, '0')}`
    this.productionToCreate.name = `${this.productionToCreate.number}_new_${this.getAdjective()}_production`
  },

  watch: {
    'productionToCreate.name'() {
      this.productionToCreate.number = this.productionToCreate.name.slice(0, 6)
    }
  }
}
</script>

<style lang="scss" scoped>
.page {
  padding-top: 60px;
  margin-left: 300px;
}

.tabs ul {
  margin-left: 0;
}

.tabs ul li {
  margin-right: 2em;
  margin-bottom: -1px;
  padding-bottom: 1em;
}

.is-active {
  color: #00b242;
  border-color: #00b242;
  border-bottom-style: solid;
  border-bottom-width: 1px;
}

.new-production {
  font-family: 'Lato';
}

.hero {
  background-color: inherit;
}

h2 {
  color: var(--text);
  font-size: 20px;
  letter-spacing: 5px;
  line-height: 24px;
  font-weight: 700;
  padding-bottom: 0.7em;
  text-transform: uppercase;
}

h1.title {
  color: var(--text);
  font-weight: 700;
  font-size: 48px;
  line-height: 56px;
  text-transform: capitalize;
}

h2.subtitle {
  border-bottom: 0;
}

.new-production > .columns {
  padding-bottom: 3rem;
}

span.input-separator {
  display: inline-block;
  padding: 3px;

  &.mr0 {
    margin-right: 0;
  }
}

.date-picker-wrapper {
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
}

.task-type {
  margin-top: 4px;
}

.task-type.task-type-name:hover {
  cursor: grab;
}

.task-type.task-type-name {
  margin-right: 5px;
  margin-bottom: 5px;
  height: 2.3rem;
}

.inline-task-type-combo {
  display: inline-flex;
  width: auto;
  min-width: auto;
  border: 0;
}

.import-content {
  display: flex;
  align-items: center;
}

.w600 {
  max-width: 600px;
}

.flexrow-item {
  margin-right: 1em;
}

.flexrow-item.mr0 {
  margin-right: 0em;
}

.flexrow-item.mr05 {
  margin-right: 0.25em;
}

.asset-type-name {
  border: 1px solid var(--text);
  border-radius: 5px;
  padding: 10px;
}

.asset-types {
  align-items: center;
}

.explaination {
  font-style: italic;
  margin-top: 0.2em;
}

.big-button {
  max-width: 100%;

  &:active,
  &:focus {
    color: white;
  }
}
.check {
  color: $white;
  background-color: $green;
  border-radius: 50%;
  height: 25px;
  width: 25px;
  display: inline-block;
  margin-bottom: -8px;
  polyline {
    transform: scale(0.5) translate(12px, 12px);
    stroke-width: 4;
  }
}

.step {
  border-radius: 50%;
  border: 2px solid $green;
  color: $green;
  height: 25px;
  font-size: 11px;
  font-weight: bold;
  padding-top: 0.2rem;
  text-align: center;
  display: inline-block;
  width: 25px;

  &.optional {
    border: 2px solid var(--text);
    color: var(--text);
  }
}

.active-confirm {
  background-color: #00b242
}

h1 {
  font-size: 200%;
  font-weight: bold;
}

p {
  margin-bottom: 2em;
}

.type-combo {
  width: 200px;
  margin-bottom: 0;
  margin-top: 4px;
}

.number-field {
  width: 120px;
  margin-right: 8px;
}
</style>
